<div class="bn-open-case-dialog flex flex-col gap-4">
  <header class="bn-open-case-dialog__header">
    <h2 class="bn-open-case-dialog__title m-0">Open a case</h2>
  </header>

  <fieldset
    class="border-0 p-0 m-0 min-w-0 flex flex-col gap-4"
    [disabled]="saving() || !!createdLiveCaseId()"
  >
    <div class="flex flex-col gap-1">
      <textarea
        id="ocd-description"
        pTextarea
        rows="2"
        aria-label="Description"
        [ngModel]="description()"
        (ngModelChange)="description.set($event)"
        placeholder="What is going on? Eviction notice, 401 contribution, medical debt."
      ></textarea>
      @if (descriptionError(); as error) {
        <p class="bn-open-case-dialog__error text-sm m-0">{{ error }}</p>
      }
    </div>

    <div class="bn-open-case-dialog__classification flex gap-3">
      <div class="bn-open-case-dialog__field flex flex-col gap-1 flex-1">
        <label for="ocd-domain" class="bn-open-case-dialog__label"
          >Domain</label
        >
        <p-select
          inputId="ocd-domain"
          [options]="domains()"
          optionLabel="label"
          optionValue="key"
          placeholder="---"
          appendTo="body"
          [ngModel]="domain()"
          (ngModelChange)="onDomainChange($event)"
        />
      </div>
      <div class="bn-open-case-dialog__field flex flex-col gap-1 flex-1">
        <label for="ocd-objective" class="bn-open-case-dialog__label"
          >Objective type</label
        >
        @if (
          selectedDomainId() !== null &&
          store.objectiveTypesLoading(selectedDomainId()!)
        ) {
          <div
            role="status"
            aria-label="Loading objective types"
            data-testid="objective-types-skeleton"
          >
            <p-skeleton width="100%" height="2.5rem" />
          </div>
        } @else {
          <p-select
            inputId="ocd-objective"
            [options]="objectiveOptions()"
            optionLabel="label"
            optionValue="key"
            placeholder="---"
            appendTo="body"
            [disabled]="
              !domain() ||
              (selectedDomainId() !== null &&
                store.objectiveTypesLoading(selectedDomainId()!))
            "
            [ngModel]="objective()"
            (ngModelChange)="onObjectiveChange($event)"
          />
        }
      </div>
    </div>
    @if (domainObjectiveError(); as error) {
      <p class="bn-open-case-dialog__error text-sm m-0">{{ error }}</p>
    }
    @if (store.isLive() && selectedDomainId(); as domainId) {
      @if (
        !store.objectiveTypesLoading(domainId) &&
          store.objectiveTypesError(domainId);
        as error
      ) {
        <div class="bn-open-case-dialog__warning text-sm" role="alert">
          <p class="m-0">{{ error }}</p>
          <button
            type="button"
            class="bn-open-case-dialog__link"
            (click)="store.loadObjectiveTypes(domainId, true)"
          >
            Retry objective types
          </button>
        </div>
      }
    }

    <div class="flex flex-col gap-1 bn-open-case-dialog__status">
      <label for="ocd-status" class="bn-open-case-dialog__label">Status</label>
      <p-select
        inputId="ocd-status"
        [options]="statusOptions"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        [ngModel]="status()"
        (ngModelChange)="status.set($event)"
      />
    </div>

    @if (
      store.goalsEnabled() &&
      (!store.isLive() || (store.goalsLoaded() && !store.goalsError()))
    ) {
      @if (store.activeGoals().length === 0 && draftGoals().length === 0) {
        <p class="text-sm text-surface-500 m-0">No active client goals yet.</p>
      } @else if (store.activeGoals().length > 0) {
        <fieldset class="flex flex-col gap-1 border-0 p-0 m-0">
          <legend class="bn-open-case-dialog__label p-0">
            Link to a client goal (optional)
          </legend>
          <div class="bn-open-case-dialog__goals flex flex-col gap-3">
            @for (goal of store.activeGoals(); track goal.id) {
              <div class="flex items-center gap-2 text-sm text-surface-700">
                <p-checkbox
                  [inputId]="'ocd-goal-' + goal.id"
                  [binary]="true"
                  [ngModel]="goalIds().includes(goal.id)"
                  (ngModelChange)="toggleGoal(goal.id, $event)"
                />
                <label [for]="'ocd-goal-' + goal.id">{{
                  goal.displayText
                }}</label>
              </div>
            }
          </div>
        </fieldset>
      }

      @for (text of draftGoals(); track $index; let i = $index) {
        <div class="bn-open-case-dialog__draft flex items-start gap-2">
          <textarea
            pTextarea
            rows="2"
            maxlength="200"
            placeholder="What the client actually wants, in their words"
            [attr.aria-label]="'New goal ' + (i + 1)"
            [ngModel]="text"
            (ngModelChange)="setDraftText(i, $event)"
          ></textarea>
          <button
            type="button"
            class="bn-open-case-dialog__action"
            (click)="removeDraftRow(i)"
          >
            Remove
          </button>
        </div>
      }

      @if (!atCap()) {
        <button
          type="button"
          class="bn-open-case-dialog__add"
          (click)="addDraftRow()"
        >
          + {{ draftGoals().length > 0 ? 'Add another goal' : 'Capture goal' }}
        </button>
      }
    }

    @if (duplicate(); as existing) {
      <div class="bn-open-case-dialog__warning text-sm" role="alert">
        <p class="m-0">
          <strong>There is already an active case for this objective.</strong>
          {{ existing.number }} · {{ statusLabel[existing.status] }} — check it
          before opening a second one.
        </p>
        <button
          type="button"
          class="bn-open-case-dialog__link"
          (click)="viewExistingCase()"
        >
          View existing case
        </button>
      </div>
    }
  </fieldset>
  @if (saveError(); as error) {
    <div class="bn-open-case-dialog__warning text-sm" role="alert">
      <p class="m-0">{{ error }}</p>
      <button
        type="button"
        class="bn-open-case-dialog__link"
        [disabled]="saving() || ambiguousGoalCreate()"
        (click)="submit()"
      >
        Retry
      </button>
    </div>
  }

  <footer class="flex items-center justify-end gap-3">
    <button
      type="button"
      class="bn-open-case-dialog__cancel"
      [disabled]="saving()"
      (click)="tryClose()"
    >
      Cancel
    </button>
    <p-button
      [rounded]="true"
      [label]="
        createdLiveCaseId()
          ? store.goalsEnabled()
            ? 'Continue saving goals'
            : 'Done'
          : duplicate() && duplicateConfirmed()
            ? 'Open it anyway'
            : 'Open case'
      "
      [disabled]="
        saving() ||
        ambiguousGoalCreate() ||
        (store.isLive() && !!domain() && objectiveOptions().length === 0)
      "
      [loading]="saving()"
      (onClick)="submit()"
    />
  </footer>
</div>

<p-confirmDialog />
